import * as React from 'react';

import omit from 'lodash/omit';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import appInfo from 'js/lib/appInfo';
import store from 'js/lib/coursera.store';
import { isAuthenticatedUser } from 'js/lib/user';

import { AUTHENTICATION_MODAL_MODES } from 'bundles/authentication/constants';
import AuthenticationModal from 'bundles/authentication/shared/components/authentication-modal/AuthenticationModal';
import useBotManager from 'bundles/authentication/shared/hooks/useBotManager';
import type { AuthModalContext } from 'bundles/authentication/shared/types/sharedTypes';
import { AUTH_PROVIDER_LOCAL_STORAGE_KEY } from 'bundles/page/common/constants';
import { applicationStoreIn, reactRouterIn } from 'bundles/page/lib/migration';
import type { AuthModalRouterQuery } from 'bundles/user-account/components/types/userAccountTypes';
import { sanitizeAuthProvider } from 'bundles/user-account/utils/AuthenticationOptionUtils';

function isAuthModeValid(authMode: string) {
  return Object.values(AUTHENTICATION_MODAL_MODES).includes(authMode);
}

type Props = {
  query: AuthModalRouterQuery;
  isEnterprise?: boolean;
  preventModalClose?: boolean;
  redirectOnCloseUrl?: string;
  utilizeWebRedirectOnClose?: boolean;
  preventRegister?: boolean;
  programId?: string;
  context?: AuthModalContext;
};

/**
 * To be used wherever authentication is needed. Abstracts over SSR-compatible AuthenticationModal
 * and older userModal.
 */
class AuthenticationOption extends React.Component<Props> {
  static contextTypes = {
    router: PropTypes.object.isRequired,

    // required by applicationStoreIn
    getStore: PropTypes.func,
  };

  declare context: LegacyContextType<typeof AuthenticationOption.contextTypes>;

  componentDidMount() {
    // check if authProvider exists, if yes, write the sanitized string to localStorage
    const { query } = this.props;
    if (query?.authProvider) {
      const sanitizedAuthProviderString = sanitizeAuthProvider(query.authProvider);
      if (sanitizedAuthProviderString) {
        // @ts-expect-error TSMIGRATION
        store.setUnserialized(AUTH_PROVIDER_LOCAL_STORAGE_KEY, sanitizedAuthProviderString);
      }
    }
  }

  shouldComponentUpdate(nextProps: Props) {
    const { query, context } = this.props;
    const { arkoseBotManagerContext } = context || {};
    const { canSwitchArkoseKey } = arkoseBotManagerContext || {};

    return (
      nextProps.query !== query || nextProps.context?.arkoseBotManagerContext?.canSwitchArkoseKey !== canSwitchArkoseKey
    );
  }

  /**
   * If userModal should be used, render that region and let the userModal code
   * decide whether to appear.
   *
   * If AuthenticationModal should be used, decide here whether it should appear.
   * @return {String} authMode: [login, signup, legacy, null]
   */
  getValidAuthMode() {
    if (isAuthenticatedUser()) {
      // no need for auth
      return null;
    }

    const { query } = this.props;
    const authMode = query?.authMode;

    if (!reactRouterIn(this)) {
      // Origami apps already have userModal enabled
      return null;
    }

    if (applicationStoreIn(this)) {
      if (!authMode) {
        return null;
      } else if (isAuthModeValid(authMode)) {
        return authMode;
      } else {
        if (typeof window !== 'undefined') {
          this.updateRouterParams();
        }
        return null;
      }
    } else {
      return 'legacy';
    }
  }

  updateRouterParams() {
    const { router } = this.context;
    const {
      location: { pathname, query },
      params,
    } = router;
    const newQueryParams = omit(query, ['authMode']);

    router.push({
      pathname,
      params,
      query: newQueryParams,
    });
  }

  render() {
    const {
      query,
      isEnterprise,
      programId,
      preventModalClose,
      preventRegister,
      redirectOnCloseUrl,
      utilizeWebRedirectOnClose,
      context,
    } = this.props;
    const authMode = this.getValidAuthMode();

    if (!authMode) {
      return null;
    } else if (authMode === 'legacy') {
      // Non-Fluxible React apps cannot use AuthenticationModal so need to use userModal.
      // However, returning null instead of userModal since log in and sign up functionality was removed from userModal (https://phabricator.dkandu.me/D81959) and userModal is being deleted.
      // Once all apps migrated from Backbone to React, can remove this else if statement entirely.
      return null;
    } else {
      // not null, and must be valid because of how it is set on state
      // Fluxible apps can use AuthenticationModal
      return (
        <AuthenticationModal
          onCloseRedirectURL={redirectOnCloseUrl}
          utilizeWebRedirectOnClose={utilizeWebRedirectOnClose}
          preventModalClose={preventModalClose}
          preventRegister={preventRegister}
          isEnterprise={isEnterprise}
          defaultMode={authMode}
          programId={programId}
          context={context}
        />
      );
    }
  }
}

// In order to move fast, using function component wrapper to use hook, we should clean it up
// once migrating the above AuthenticationOption to a function component
const AuthenticationOptionWrapper = (props: Props) => {
  // We need to bypass Arkose initialization on degree and teams pages
  // that include another authentication form with a separate Arkose initialization.
  // Without this bypass, Arkose would be initialized twice, leading to potential race conditions
  // and conflicts with `window.enforcement`.
  // [Design doc](https://docs.google.com/document/d/11rrksObuKCI5hCI5jQeuyDuB3W7vGMK-_0zKd9Xsau0/edit?tab=t.0#bookmark=id.a1s4eggqi9gb)
  const ARKOSE_BYPASS_APP = ['xddp', 'degree-description', 'teams-v2'];
  const appName = appInfo.get().appName || '';
  const shouldBypassArkose = ARKOSE_BYPASS_APP.includes(appName) || isAuthenticatedUser();

  const { arkoseBotManager, isRetrievingTokenRef, canSwitchArkoseKey } = useBotManager({
    shouldBypassRecaptcha: true,
    shouldBypassArkose,
  });

  const context = {
    arkoseBotManagerContext: shouldBypassArkose
      ? undefined
      : {
          arkoseBotManager,
          isRetrievingTokenRef,
          canSwitchArkoseKey,
        },
  };

  return <AuthenticationOption {...props} context={context} />;
};

export default AuthenticationOptionWrapper;
